import React, { useState, useEffect, useRef } from "react";
import {
  FaTimes,
  FaPaw,
  FaUser,
  FaCalendarAlt,
  FaUserTie,
  FaCheck,
  FaBan,
  FaBed,
  FaTag,
  FaEdit,
} from "react-icons/fa";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import OrderNumberBadge from "./OrderNumberBadge";
import StaffSelector from "../../pages/Orders/StaffSelector";
import LoaderSpiner from "../LoaderSpiner/LoaderSpiner";
import { useColor } from "../../hooks/color/useColor";
import { httpService } from "../../services/httpService";
import OrderFormsSection from "./OrderFormsSection";

// Helper functions
function parseTimeRange(timeRange) {
  if (!timeRange) return null;
  try {
    // Handle different separators
    const separators = ["–", "-", "to", "TO"];
    let startTime = "";
    let endTime = "";

    for (const sep of separators) {
      if (timeRange.includes(sep)) {
        const parts = timeRange.split(sep).map((p) => p.trim());
        if (parts.length >= 2) {
          startTime = parts[0];
          endTime = parts[1];
          break;
        }
      }
    }

    // If no separator found, treat the whole string as start time
    if (!startTime && !endTime) {
      startTime = timeRange.trim();
    }

    return { startTime, endTime };
  } catch (error) {
    console.error("Error parsing time range:", error);
    return null;
  }
}

function parseTimeToDate(timeStr) {
  if (!timeStr) return null;
  try {
    const cleanTime = timeStr.trim().toUpperCase();
    const time = new Date(`2000-01-01 ${cleanTime}`);
    if (!isNaN(time.getTime())) return time;

    const match = cleanTime.match(/(\d+):?(\d*)\s*(AM|PM)?/i);
    if (match) {
      let hours = parseInt(match[1], 10);
      const minutes = match[2] ? parseInt(match[2], 10) : 0;
      const meridiem = (match[3] || "").toUpperCase();
      if (meridiem === "PM" && hours < 12) hours += 12;
      if (meridiem === "AM" && hours === 12) hours = 0;
      return new Date(2000, 0, 1, hours, minutes);
    }
    return null;
  } catch (error) {
    console.error("Error parsing time:", error);
    return null;
  }
}

function getDurationMinutes(duration) {
  if (!duration) return 0;

  const match = String(duration).match(/\d+/);

  return match ? parseInt(match[0], 10) : 0;
}

function isTimeOverlapping(start1, duration1, start2, duration2) {
  const s1 = parseTimeToDate(start1);
  const s2 = parseTimeToDate(start2);

  if (!s1 || !s2) return false;

  const e1 = new Date(s1.getTime() + duration1 * 60000);

  const e2 = new Date(s2.getTime() + duration2 * 60000);

  return s1 < e2 && s2 < e1;
}

function isDateOverlapping(startDate1, endDate1, startDate2, endDate2) {
  if (!startDate1 || !endDate1 || !startDate2 || !endDate2) return false;

  const s1 = new Date(startDate1);
  const e1 = new Date(endDate1);
  const s2 = new Date(startDate2);
  const e2 = new Date(endDate2);

  // Check if date ranges overlap
  return s1 < e2 && s2 < e1;
}

function generateTimeSlotsFromRange(startTime, endTime, interval = 15) {
  const slots = [];
  const start = parseTimeToDate(startTime);
  const end = parseTimeToDate(endTime);

  if (!start || !end) return generateDefaultTimeSlots();

  let current = new Date(start);
  while (current <= end) {
    const formatted = current.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    slots.push(formatted);
    current = new Date(current.getTime() + interval * 60 * 1000);
  }
  return slots;
}

function generateDefaultTimeSlots() {
  const slots = [];
  let start = 8 * 60;
  const end = 20 * 60;
  while (start <= end) {
    const hours = Math.floor(start / 60);
    const minutes = start % 60;
    const formatted = new Date(0, 0, 0, hours, minutes).toLocaleTimeString(
      "en-US",
      { hour: "numeric", minute: "2-digit", hour12: true },
    );
    slots.push(formatted);
    start += 15;
  }
  return slots;
}

function formatDisplayDate(isoDate) {
  if (!isoDate) return "";

  const d = new Date(isoDate);

  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();

  return `${dd}/${mm}/${yyyy}`;
}

function getFormattedDuration(item) {
  const duration = item?.duration;

  if (!duration) return "";
  if (
    typeof duration === "string" &&
    duration.toLowerCase().includes("minute")
  ) {
    return duration;
  }

  if (item.item_type === "boarding") {
    return `${duration} Day${Number(duration) > 1 ? "s" : ""}`;
  }

  if (item.item_type === "vet_clinic") {
    return `${duration} Minutes`;
  }

  return duration;
}

function formatDateForInput(isoDate) {
  if (!isoDate) return "";
  const d = new Date(isoDate);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

// Extract start time from time_range string
function extractStartTime(timeRange) {
  if (!timeRange) return "";
  const parsed = parseTimeRange(timeRange);
  return parsed?.startTime || "";
}

 // ─── Business hours fetching & slot building (mirrors BookingOrderTooltip fix) ───

async function fetchBusinessHoursFor(itemType) {
  try {
    let endpoint = "";
    if (itemType === "grooming")   endpoint = "/groominghours/getgroomingbusinesshours";
    if (itemType === "vet_clinic") endpoint = "/vethours/getvetbusinesshours";
    if (!endpoint) return null;

    const res = await httpService.get(endpoint);
    return res.data?.data || res.data || null;
  } catch (err) {
    console.warn("fetchBusinessHoursFor failed:", err);
    return null;
  }
}

function parseTimeMinutes(timeStr) {
  if (!timeStr) return 0;
  const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (!match) return 0;
  let h = parseInt(match[1]);
  const m = parseInt(match[2]);
  const meridiem = match[3].toUpperCase();
  if (meridiem === "PM" && h !== 12) h += 12;
  if (meridiem === "AM" && h === 12) h = 0;
  return h * 60 + m;
}

// Business-hours-aware slot builder for a single date, correctly handling
// "Custom Hours" isOpen:true (slots ARE the open window) vs isOpen:false
// (slots are a BLOCKED window inside an otherwise normal day).
function buildBusinessHourSlotsForDate(dateStr, bh) {
  if (!bh || !dateStr) return null; // null = no config, caller should fall back

  const dayNames = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
  const [yr, mo, dy] = dateStr.split("-").map(Number);
  const dayName = dayNames[new Date(yr, mo - 1, dy).getDay()];
  const weekDay = bh.weeklySchedule?.find((d) => d.day === dayName);

  const specialDay = bh.specialDays?.find((sd) => sd.date?.split("T")[0] === dateStr);

  let sourceSlots = [];
  let blockedSlots = [];

  if (specialDay) {
    if (specialDay.label === "Closed") return [];
    if (specialDay.label === "Full Day") {
      sourceSlots = bh.timeSlots || [];
    } else if (specialDay.label === "Custom Hours") {
      if (specialDay.isOpen) {
        sourceSlots = specialDay.slots || [];
      } else {
        if (!weekDay?.isOpen) return [];
        sourceSlots = bh.timeSlots || [];
        blockedSlots = specialDay.slots || [];
      }
    }
  } else {
    if (!weekDay?.isOpen) return [];
    sourceSlots = bh.timeSlots || [];
    if (!sourceSlots.length) return null; // no config at all
  }

  const isBlocked = (mins) =>
    blockedSlots.some(({ start, end }) => mins >= parseTimeMinutes(start) && mins < parseTimeMinutes(end));

  const slots = [];
  sourceSlots.forEach(({ start: s, end: e }) => {
    let cur = parseTimeMinutes(s);
    const endMin = parseTimeMinutes(e);
    while (cur < endMin) {
      if (!isBlocked(cur)) {
        slots.push(
          new Date(0, 0, 0, Math.floor(cur / 60), cur % 60)
            .toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })
        );
      }
      cur += 15;
    }
  });
  return slots;
}

// Accordion Section wrapper
const ServiceSection = ({ title, headerMeta, children }) => {
  const [open, setOpen] = useState(true);

  return (
    <div
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: "10px",
        overflow: "hidden",
        marginBottom: "10px",
      }}
    >
      <div
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 14px",
          cursor: "pointer",
          background: "#fff",
          borderBottom: open ? "1px solid #e5e7eb" : "none",
          userSelect: "none",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "14px",
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontWeight: 800, fontSize: "17px", color: "#111827" }}>
            {title}
          </span>
          {headerMeta}
        </div>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#6b7280"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            transform: open ? "rotate(0deg)" : "rotate(180deg)",
            transition: "transform 0.2s",
            flexShrink: 0,
          }}
        >
          <polyline points="18 15 12 9 6 15" />
        </svg>
      </div>

      {open && (
        <div style={{ padding: "12px 14px", background: "#fff" }}>
          {children}
        </div>
      )}
    </div>
  );
};

const isItemFullyAssigned = (item, idx, selectedStaff, selectedTimes, selectedRooms) => {
  if (item.item_type === "boarding") {
    return !!selectedRooms[idx];
  }
  if (["grooming", "vet_clinic", "dog_walking"].includes(item.item_type)) {
    return !!selectedStaff[idx] && !!selectedTimes[idx];
  }
  return !!selectedStaff[idx];
};

const OnlineOrderPopup = ({
  isOpen,
  orderData,
  onClose,
  onViewOrder,
  onRescheduleClick,
  employees = [],
  refreshData,
  onAcceptSuccess,
}) => {
  const ref = useRef(null);
  const { getColor } = useColor();

  const [loading, setLoading] = useState(false);
  const [processedOrder, setProcessedOrder] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState({});
  const [selectedTimes, setSelectedTimes] = useState({});
  const [selectedRooms, setSelectedRooms] = useState({});
  const [roomTypesData, setRoomTypesData] = useState({});
  const [roomsData, setRoomsData] = useState({});
  const [fetchingRooms, setFetchingRooms] = useState({});
  const [roomAvailabilityMessage, setRoomAvailabilityMessage] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [itemBusinessHours, setItemBusinessHours] = useState({});
  const [bypassState, setBypassState] = useState({});


  const getAvailableRoomsForDropdown = (itemIndex) => {
    const currentItem = processedOrder?.order_items?.[itemIndex];
    if (!currentItem) return [];

    // Get rooms from roomsData which should already be filtered by room type
    const availableRooms = roomsData[itemIndex] || [];
    const currentSelectedRoom = selectedRooms[itemIndex];

    const roomTypeId = currentItem.room_type_id;

    // Get all selected rooms from other boarding items with their date information
    const otherSelectedRooms = Object.entries(selectedRooms)
      .filter(([idx, roomId]) => {
        const index = Number(idx);
        if (index === itemIndex) return false;
        const item = processedOrder?.order_items?.[index];
        return item?.item_type === "boarding" && roomId;
      })
      .map(([idx, roomId]) => ({
        itemIndex: Number(idx),
        roomId: roomId,
        item: processedOrder?.order_items?.[Number(idx)],
      }));

    return availableRooms.filter((room) => {
      if (currentSelectedRoom === room._id) {
        return true;
      }

      const isAlreadyBooked = otherSelectedRooms.some(({ roomId, item }) => {
        if (roomId !== room._id) return false;

        // Check date overlap
        return isDateOverlapping(
          currentItem.start_date,
          currentItem.end_date,
          item.start_date,
          item.end_date,
        );
      });

      return !isAlreadyBooked;
    });
  };

  // Fetch available rooms for a boarding item
  const fetchAvailableRoomsForItem = async (item, itemIndex) => {
    if (!item.start_date || !item.end_date || !item.pet_type) return;

    setFetchingRooms((prev) => ({ ...prev, [itemIndex]: true }));
    setRoomAvailabilityMessage((prev) => ({
      ...prev,
      [itemIndex]: "Checking room availability...",
    }));

    try {
      const payload = {
        checkIn: formatDateForInput(item.start_date),
        checkOut: formatDateForInput(item.end_date),
        type: "boarding",
        petType: item.pet_type || "Dog",
      };

      const response = await httpService.post(
        "/room/getAvailableVendorRooms",
        payload,
      );

      let roomTypes = response.data;

      if (!roomTypes || roomTypes.length === 0) {
        setRoomTypesData((prev) => ({ ...prev, [itemIndex]: [] }));
        setRoomAvailabilityMessage((prev) => ({
          ...prev,
          [itemIndex]: `No rooms available for ${item.pet_type} on selected dates.`,
        }));
        setRoomsData((prev) => ({ ...prev, [itemIndex]: [] }));
      } else {
        // Process room types and filter rooms
        const filteredRoomTypes = roomTypes
          .map((roomType) => {
            const allRooms =
              roomType.available_room_numbers || roomType.room_numbers || [];

            const availableRooms = allRooms.filter((room) => {
              const isAlreadyBooked = Object.entries(selectedRooms).some(
                ([otherIdx, otherRoomId]) => {
                  const otherIndex = Number(otherIdx);
                  if (otherIndex === itemIndex) return false;

                  const otherItem = processedOrder?.order_items?.[otherIndex];
                  if (!otherItem || otherItem.item_type !== "boarding")
                    return false;

                  if (otherRoomId === room._id) {
                    return isDateOverlapping(
                      item.start_date,
                      item.end_date,
                      otherItem.start_date,
                      otherItem.end_date,
                    );
                  }
                  return false;
                },
              );

              return !isAlreadyBooked;
            });

            if (availableRooms.length > 0) {
              return {
                ...roomType,
                available_room_numbers: availableRooms,
                room_numbers: availableRooms,
              };
            }
            return null;
          })
          .filter(Boolean);

        // console.log(
        //   "Filtered Room Types for item",
        //   itemIndex,
        //   ":",
        //   filteredRoomTypes.map((rt) => ({
        //     id: rt._id,
        //     name: rt.room_name,
        //     availableRooms: rt.available_room_numbers.map((r) => r.room_number),
        //   })),
        // );

        setRoomTypesData((prev) => ({
          ...prev,
          [itemIndex]: filteredRoomTypes,
        }));

        const originalRoomTypeId = item.room_type_id;
        const originalRoomTypeAvailable = filteredRoomTypes.some(
          (rt) => rt._id === originalRoomTypeId,
        );

        if (originalRoomTypeAvailable) {
          // Original room type has available rooms - use it
          setRoomAvailabilityMessage((prev) => ({
            ...prev,
            [itemIndex]: `Available rooms for ${item.room_name || "Room Type"}`,
          }));

          handleRoomTypeChange(
            itemIndex,
            originalRoomTypeId,
            filteredRoomTypes,
          );
        } else {
          setRoomAvailabilityMessage((prev) => ({
            ...prev,
            [itemIndex]: `No rooms available for ${item.room_name || "this room type"}`,
          }));

          setRoomsData((prev) => ({ ...prev, [itemIndex]: [] }));

          // Clear selected room
          if (selectedRooms[itemIndex]) {
            setSelectedRooms((prev) => ({ ...prev, [itemIndex]: "" }));
          }
        }
      }
    } catch (error) {
      console.error("Error fetching available rooms:", error);
      setRoomAvailabilityMessage((prev) => ({
        ...prev,
        [itemIndex]: "Error loading available rooms",
      }));
      setRoomTypesData((prev) => ({ ...prev, [itemIndex]: [] }));
      setRoomsData((prev) => ({ ...prev, [itemIndex]: [] }));
    } finally {
      setFetchingRooms((prev) => ({ ...prev, [itemIndex]: false }));
    }
  };
  // Handle room type change
  const handleRoomTypeChange = (
    itemIndex,
    roomTypeId,
    roomTypesList = roomTypesData[itemIndex] || [],
  ) => {
    // Find the selected room type
    const selectedType = roomTypesList.find((rt) => rt._id === roomTypeId);

    if (selectedType) {
      // Get rooms ONLY from this specific room type
      let availableRooms =
        selectedType.available_room_numbers || selectedType.room_numbers || [];

      const currentItem = processedOrder?.order_items?.[itemIndex];
      if (currentItem) {
        const otherSelectedRooms = Object.entries(selectedRooms)
          .filter(([idx, roomId]) => {
            const index = Number(idx);
            if (index === itemIndex) return false;
            const item = processedOrder?.order_items?.[index];
            return item?.item_type === "boarding" && roomId;
          })
          .map(([idx, roomId]) => ({
            itemIndex: Number(idx),
            roomId: roomId,
            item: processedOrder?.order_items?.[Number(idx)],
          }));

        availableRooms = availableRooms.filter((room) => {
          if (selectedRooms[itemIndex] === room._id) {
            return true;
          }

          const isBookedElsewhere = otherSelectedRooms.some(
            ({ roomId, item }) => {
              if (roomId !== room._id) return false;
              return isDateOverlapping(
                currentItem.start_date,
                currentItem.end_date,
                item.start_date,
                item.end_date,
              );
            },
          );

          return !isBookedElsewhere;
        });
      }

      // IMPORTANT: Set roomsData ONLY for this specific room type
      setRoomsData((prev) => ({
        ...prev,
        [itemIndex]: availableRooms,
      }));

      // If current selected room is not in available rooms, clear it
      if (
        selectedRooms[itemIndex] &&
        !availableRooms.some((r) => r._id === selectedRooms[itemIndex])
      ) {
        setSelectedRooms((prev) => ({ ...prev, [itemIndex]: "" }));
      }
    } else {
      // If no room type selected, clear rooms
      setRoomsData((prev) => ({
        ...prev,
        [itemIndex]: [],
      }));
    }
  };

  // useEffect(() => {
  //   if (!isOpen || !orderData) return;

  //   const processOrderItems = async () => {
  //     setLoading(true);
  //     try {
  //       const processedItems = (orderData.order_items || []).map((item) => {
  //         let timeSlots = [];
  //         let initialTime = "";

  //         // Handle time slots for grooming/vet appointments
  //         if (
  //           item.item_type === "grooming" ||
  //           item.item_type === "vet_clinic"
  //         ) {
  //           // Parse the time_range to get start and end times
  //           const timeRange = parseTimeRange(
  //             item.time_range || item.start_time,
  //           );
  //           if (timeRange && timeRange.startTime && timeRange.endTime) {
  //             timeSlots = generateTimeSlotsFromRange(
  //               timeRange.startTime,
  //               timeRange.endTime,
  //             );
  //             initialTime = timeRange.startTime;
  //           } else {
  //             timeSlots = generateDefaultTimeSlots();
  //             initialTime =
  //               extractStartTime(item.start_time) || timeSlots[0] || "";
  //           }
  //         }

  //         return {
  //           ...item,
  //           parsedTimeSlots: timeSlots,
  //           isPast: isPastAppointment(item),
  //           initialStaff: item.technician_id || "",
  //           initialTime: initialTime,
  //           displayStartDate: formatDisplayDate(item.start_date),
  //           displayEndDate: item.end_date
  //             ? formatDisplayDate(item.end_date)
  //             : null,
  //         };
  //       });

  //       setProcessedOrder({
  //         ...orderData,
  //         order_items: processedItems,
  //         displayClientName: `${orderData.client_first_name || ""} ${
  //           orderData.client_last_name || ""
  //         }`.trim(),
  //         displayClientPhone: orderData.client_phone,
  //         displayClientEmail: orderData.client_email,
  //       });

  //       const initialStaff = {};
  //       const initialTimes = {};
  //       const initialRooms = {};

  //       processedItems.forEach((item, idx) => {
  //         initialStaff[idx] = item.initialStaff;
  //         initialTimes[idx] = "";
  //         initialRooms[idx] = item.room_id || "";

  //         // Fetch available rooms for boarding items
  //         if (item.item_type === "boarding") {
  //           fetchAvailableRoomsForItem(item, idx);
  //         }
  //       });

  //       setSelectedStaff(initialStaff);
  //       setSelectedTimes(initialTimes);
  //       setSelectedRooms(initialRooms);
  //     } catch (error) {
  //       console.error("Error processing order:", error);
  //       toast.error("Failed to load order details");
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   processOrderItems();
  // }, [isOpen, orderData]);

  useEffect(() => {
    if (!isOpen || !orderData) return;

    const processOrderItems = async () => {
      setLoading(true);
      try {
        const processedItems = await Promise.all(
          (orderData.order_items || []).map(async (item) => {
            let timeSlots = [];
            let initialTime = "";

            if (item.item_type === "grooming" || item.item_type === "vet_clinic") {
              const dateStr = item.start_date?.split("T")[0] || "";
              const bh = await fetchBusinessHoursFor(item.item_type);

              const bhSlots = bh ? buildBusinessHourSlotsForDate(dateStr, bh) : null;

              if (bhSlots !== null) {
                timeSlots = bhSlots;
                const timeRange = parseTimeRange(item.time_range || item.start_time);
                initialTime = timeRange?.startTime || extractStartTime(item.start_time) || timeSlots[0] || "";
              } else {
                const timeRange = parseTimeRange(item.time_range || item.start_time);
                if (timeRange && timeRange.startTime && timeRange.endTime) {
                  timeSlots = generateTimeSlotsFromRange(timeRange.startTime, timeRange.endTime);
                  initialTime = timeRange.startTime;
                } else {
                  timeSlots = generateDefaultTimeSlots();
                  initialTime = extractStartTime(item.start_time) || timeSlots[0] || "";
                }
              }
            }

            return {
              ...item,
              parsedTimeSlots: timeSlots,
              isPast: isPastAppointment(item),
              initialStaff: item.technician_id || "",
              initialTime: initialTime,
              displayStartDate: formatDisplayDate(item.start_date),
              displayEndDate: item.end_date ? formatDisplayDate(item.end_date) : null,
            };
          })
        );

        setProcessedOrder({
          ...orderData,
          order_items: processedItems,
          displayClientName: `${orderData.client_first_name || ""} ${
            orderData.client_last_name || ""
          }`.trim(),
          displayClientPhone: orderData.client_phone,
          displayClientEmail: orderData.client_email,
        });

        const initialStaff = {};
        const initialTimes = {};
        const initialRooms = {};

        processedItems.forEach((item, idx) => {
          initialStaff[idx] = item.initialStaff;
          initialTimes[idx] = "";
          initialRooms[idx] = item.room_id || "";

          if (item.item_type === "boarding") {
            fetchAvailableRoomsForItem(item, idx);
          }
        });

        setSelectedStaff(initialStaff);
        setSelectedTimes(initialTimes);
        setSelectedRooms(initialRooms);
      } catch (error) {
        console.error("Error processing order:", error);
        toast.error("Failed to load order details");
      } finally {
        setLoading(false);
      }
    };

    processOrderItems();
  }, [isOpen, orderData]);

  const handleStaffChange = (itemIndex, staffId) => {
    setSelectedStaff((prev) => ({ ...prev, [itemIndex]: staffId }));
  };

  const handleTimeChange = (itemIndex, time) => {
    setSelectedTimes((prev) => ({ ...prev, [itemIndex]: time }));
  };

  const handleRoomChange = (itemIndex, roomId) => {
    setSelectedRooms((prev) => ({ ...prev, [itemIndex]: roomId }));

    const currentItem = processedOrder?.order_items?.[itemIndex];
    if (currentItem?.item_type === "boarding") {
      processedOrder?.order_items?.forEach((item, idx) => {
        if (item.item_type === "boarding" && idx !== itemIndex) {
          setTimeout(() => {
            fetchAvailableRoomsForItem(item, idx);
          }, 100);
        }
      });
    }
  };

  const getUnavailableStaffIds = (itemIndex) => {
    const currentItem = processedOrder?.order_items?.[itemIndex];
    if (!currentItem) return new Set();

    const currentStartTime = selectedTimes[itemIndex];
    const currentDuration = getDurationMinutes(currentItem?.duration);

    // If no time selected yet for this item, nothing to conflict against
    if (!currentStartTime) return new Set();

    const unavailable = new Set();

    Object.entries(selectedStaff).forEach(([idx, staffId]) => {
      const index = Number(idx);
      if (index === itemIndex || !staffId) return;

      const compareItem = processedOrder?.order_items?.[index];
      if (!compareItem) return;

      if (compareItem.start_date !== currentItem.start_date) return;

      const compareStartTime = selectedTimes[index];
      const compareDuration = getDurationMinutes(compareItem?.duration);

      // Only block if the other service also has a time selected
      if (!compareStartTime) return;

      if (
        isTimeOverlapping(
          currentStartTime,
          currentDuration,
          compareStartTime,
          compareDuration,
        )
      ) {
        unavailable.add(staffId);
      }
    });

    return unavailable;
  };

  const handleStaffSelectorChange = (itemIndex, field, value) => {
    if (field !== "technician_id") return;

    const currentItem = processedOrder?.order_items?.[itemIndex];

    const currentStartTime = selectedTimes[itemIndex];

    const currentDuration = getDurationMinutes(currentItem?.duration);

    const isBusy = Object.entries(selectedStaff).some(([idx, staffId]) => {
      const index = Number(idx);

      if (index === itemIndex) return false;

      if (staffId !== value) return false;

      const compareItem = processedOrder?.order_items?.[index];

      if (compareItem?.start_date !== currentItem?.start_date) {
        return false;
      }

      const compareStartTime = selectedTimes[index];

      const compareDuration = getDurationMinutes(compareItem?.duration);

      return isTimeOverlapping(
        currentStartTime,
        currentDuration,
        compareStartTime,
        compareDuration,
      );
    });

    setSelectedStaff((prev) => ({
      ...prev,
      [itemIndex]: value,
    }));
  };

   // --- new helper ---
function isPastAppointment(item) {
  if (item.item_type === "boarding") {
    // Boarding stays valid (editable) until checkout date has fully passed
    const endDateStr = item.end_date;
    if (!endDateStr) return false;

    const endDate = new Date(endDateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);

    return endDate < today; // only past once checkout date itself is gone
  }
  // Grooming / vet_clinic: check start_date + time.
  const dateStr = item.start_date;
  if (!dateStr) return false;

  const itemDate = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  itemDate.setHours(0, 0, 0, 0);

  if (itemDate < today) return true;

  // Same-day: also check time for grooming/vet items
  // if (itemDate.getTime() === today.getTime()) {
  //   const startTime = extractStartTime(item.time_range || item.start_time);
  //   const t = parseTimeToDate(startTime);
  //   if (t) {
  //     const now = new Date();
  //     const compare = new Date(2000, 0, 1, now.getHours(), now.getMinutes());
  //     return t < compare;
  //   }
  // }
  return false;
}

// filter same-day slots so past times aren't offered
function filterPastSlots(slots, item) {
  const today = new Date();
  const itemDate = new Date(item.start_date);
  today.setHours(0, 0, 0, 0);
  itemDate.setHours(0, 0, 0, 0);
  if (itemDate.getTime() !== today.getTime()) return slots; // future day, no filtering needed

  const now = new Date();
  const nowCompare = new Date(2000, 0, 1, now.getHours(), now.getMinutes());
  return slots.filter((slot) => {
    const t = parseTimeToDate(slot);
    return t && t >= nowCompare;
  });
}

  const handleRejectOrder = async () => {
    const result = await Swal.fire({
      title: "Reject Order?",
      text: "Are you sure you want to reject this entire order?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, Reject",
    });
    if (!result.isConfirmed) return;

    setIsRejecting(true);
    try {
      const orderId = processedOrder?._id || processedOrder?.order_id;

      if (!orderId) {
        toast.error("Order ID is missing. Unable to reject order.");
        return;
      }
      await httpService.post("/vendororder/cancelOrder", {
        order_id: orderId,
      });

      toast.success("Order rejected successfully");
      if (refreshData) {
        await refreshData();
      }
      setTimeout(() => onClose(), 500);
    } catch (err) {
      console.error("Error rejecting order:", err);
      toast.error(err?.response?.data?.message || "Failed to reject order");
    } finally {
      setIsRejecting(false);
    }
  };

  const handleAcceptOrder = async () => {
    setIsSaving(true);
    try {
      const acceptPromises = processedOrder.order_items.map((item, idx) => {
        const isBoarding = item.item_type === "boarding";
        const isAppointment = [
          "grooming",
          "vet_clinic",
          "dog_walking",
        ].includes(item.item_type);

        const payload = {
          orderId: processedOrder._id,
          refId: item.ref_id,
          itemType: item.item_type,
          status: "confirmed", // ✅ direct status, no action field
        };

        // ✅ include staff if selected
        if (isAppointment && selectedStaff[idx]) {
          payload.employee_id = selectedStaff[idx];
        }
        if (isAppointment && selectedTimes[idx]) {
          payload.time_slot = selectedTimes[idx];
        }

        // ✅ include room if selected
        if (isBoarding && selectedRooms[idx]) {
          payload.room_id = selectedRooms[idx];
          payload.room_type_id = item.room_type_id || null;
        }

        return httpService.post("/vendororder/updateOnlineItemStatus", payload);
      });

      await Promise.all(acceptPromises);

      const allAssigned = processedOrder.order_items.every((item, idx) =>
        isItemFullyAssigned(item, idx, selectedStaff, selectedTimes, selectedRooms),
      );

      toast.success(
        allAssigned
          ? "Order accepted and confirmed successfully!"
          : "Order accepted! Unassigned items moved to queue.",
      );

      const orderId = processedOrder._id || processedOrder.order_id;
      const flow = allAssigned ? "scheduled" : "queue";

      if (refreshData) {
        await refreshData();
      }

      if (onAcceptSuccess) {
        onAcceptSuccess({ orderId, flow, highlightRefId: processedOrder.order_items?.[0]?.ref_id });
        onClose();
      } else {
        setTimeout(() => onClose(), 500);
      }
    } catch (error) {
      //console.error("❌ Error accepting order:", error);
      toast.error(
        error?.response?.data?.message ||
          "Failed to accept order. Please try again.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        e.target.closest(".swal2-container") ||
        e.target.closest(".swal2-popup") ||
        e.target.closest(".reschedule-drawer-wrapper")
      )
        return;
      if (ref.current && !ref.current.contains(e.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose]);

  if (!isOpen || !processedOrder) return null;
  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm">
        <LoaderSpiner text="Loading..." />
      </div>
    );
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />

      <div
        ref={ref}
        className="fixed z-50 bg-white flex flex-col overflow-hidden"
        style={{
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          width: "660px",
          maxWidth: "96vw",
          maxHeight: "90vh",
          borderRadius: "16px",
          boxShadow: "0 24px 60px rgba(0,0,0,0.22)",
          fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
        }}
      >
        {/* HEADER */}
        <div
          style={{
            padding: "14px 18px",
            borderBottom: "1px solid #f3f4f6",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "#fff",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
            <OrderNumberBadge
              orderNumber={processedOrder.order_number}
              orderType="online"
              className="text-sm px-4 py-2 rounded-lg font-bold shadow-sm border border-black flex items-center"
            />

            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <div
                style={{
                  width: "34px",
                  height: "34px",
                  borderRadius: "50%",
                  background: "#f3f4f6",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FaUser size={16} color="#9ca3af" />
              </div>
              <div>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: "16px",
                    color: "#111827",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    flexWrap: "wrap",
                  }}
                >
                  <span>{processedOrder.displayClientName}</span>

                  <span
                    style={{
                      fontSize: "16px",
                      fontWeight: 500,
                      color: "#111827",
                    }}
                  >
                    ({processedOrder.displayClientPhone})
                  </span>
                </div>

                <div
                  style={{
                    fontSize: "14px",
                    color: "#111827",
                    fontWeight: 500,
                    marginTop: "2px",
                  }}
                >
                  {processedOrder.displayClientEmail}
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div
              style={{
                border: "1.5px solid #16a34a",
                borderRadius: "10px",
                background: "#f0fdf4",
                padding: "7px 18px",
                fontWeight: 800,
                fontSize: "20px",
                color: "#15803d",
              }}
            >
              ₹{Math.round(processedOrder.grand_total)}
            </div>
            <button
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#9ca3af",
                padding: "4px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <FaTimes size={20} />
            </button>
          </div>
        </div>

        {/* BODY */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "12px 14px",
          }}
        >
          {processedOrder.order_items?.map((item, idx) => {
            const staffSelectorItem = {
              ...item,
              technician_id: selectedStaff[idx] || "",
              start_date: item.start_date,
              start_time: selectedTimes[idx] || "",
              duration: item.duration,
              service_category_type_id: item.service_category_type_id,
              ref_id: item.ref_id,
            };
            const isBoarding = item.item_type === "boarding";
            const isGroomingOrVet =
              item.item_type === "grooming" || item.item_type === "vet_clinic";

            const headerMeta = isBoarding ? (
              <>
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    fontSize: "12.5px",
                    color: "#6b7280",
                  }}
                >
                  <FaCalendarAlt size={13} />
                  Check-in Date
                  <span
                    style={{
                      color: "#2563eb",
                      fontWeight: 600,
                      marginLeft: "2px",
                    }}
                  >
                    {item.displayStartDate}
                  </span>
                </span>
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    fontSize: "12.5px",
                    color: "#6b7280",
                  }}
                >
                  <FaCalendarAlt size={13} />
                  Check-out Date
                  <span
                    style={{
                      color: "#2563eb",
                      fontWeight: 600,
                      marginLeft: "2px",
                    }}
                  >
                    {item.displayEndDate}
                  </span>
                </span>
                {item.isPast && (
                <span style={{ fontSize: "11px", fontWeight: 700, color: "#b91c1c", background: "#fee2e2", padding: "3px 8px", borderRadius: "6px" }}>
                  ⚠ Past Date
                </span>
              )}
              </>
            ) : isGroomingOrVet ? (
              <>
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    fontSize: "12.5px",
                    color: "#6b7280",
                  }}
                >
                  <FaCalendarAlt size={13} />
                  Date
                  <span
                    style={{
                      color: "#2563eb",
                      fontWeight: 600,
                      marginLeft: "2px",
                    }}
                  >
                    {item.displayStartDate}
                  </span>
                </span>
                <span style={{ fontSize: "12.5px", color: "#374151" }}>
                  Time Slot - {item.time_range || item.start_time || "-"}
                </span>

                {item.isPast && (
                  <span style={{ fontSize: "11px", fontWeight: 700, color: "#b91c1c", background: "#fee2e2", padding: "3px 8px", borderRadius: "6px" }}>
                    ⚠ Past Date
                  </span>
                )}
              </>
            ) : null;

            return (
              <ServiceSection
                key={item.ref_id || idx}
                title={
                  item.service_type_name ||
                  (isBoarding
                    ? "Kennel Boarding"
                    : item.item_type === "grooming"
                      ? "Grooming Salon"
                      : "Vet Clinic")
                }
                headerMeta={headerMeta}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "6px",
                  }}
                >
                  <span
                    style={{
                      fontWeight: 700,
                      fontSize: "14.5px",
                      color: "#111827",
                    }}
                  >
                    {item.service_name}
                    {getFormattedDuration(item)
                      ? ` (${getFormattedDuration(item)})`
                      : ""}
                  </span>
                  <span
                    style={{
                      fontWeight: 700,
                      fontSize: "15px",
                      color: "#111827",
                    }}
                  >
                    ₹ {Math.round(item.final_total)}
                  </span>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "7px",
                    marginBottom: "12px",
                  }}
                >
                  <FaPaw size={17} color="#f97316" />
                  <span style={{ fontSize: "13.5px", color: "#374151" }}>
                    <strong>{item.pet_name}</strong>{" "}
                    <span style={{ color: "#6b7280" }}>({item.pet_type})</span>{" "}
                    • {item.pet_size} • {item.pet_breed}
                  </span>
                </div>

                <div style={{ marginBottom: "12px" }}>
                  <OrderFormsSection
                    key={item.ref_id || item._id}
                    orderId={processedOrder._id || processedOrder.order_id}
                    item={item}
                    clientPhone={
                      processedOrder.displayClientPhone ||
                      processedOrder.client_phone ||
                      ""
                    }
                    isBypassed={bypassState[item.ref_id || item._id] || false}
                    onBypassChange={(key, checked) => {
                      setBypassState((prev) => ({ ...prev, [key]: checked }));
                    }}
                  />
                </div>

                {/* Boarding fields with dynamic room selection */}
                {isBoarding && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      flexWrap: "wrap",
                    }}
                  >
                    {/* Room Type Label */}
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#111827",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Room Type
                    </div>

                    {/* Room Type Input */}
                    <div
                      style={{
                        width: "170px",
                        border: "1px solid #d1d5db",
                        borderRadius: "8px",
                        padding: "9px 12px",
                        fontSize: "13.5px",
                        background: "#fff",
                        color: "#111827",
                        fontWeight: 400,
                        height: "40px",
                        display: "flex",
                        alignItems: "center",
                        boxSizing: "border-box",
                      }}
                    >
                      {item.room_name || "—"}
                    </div>

                    {/* Room Number Label*/}
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#111827",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Room Number
                    </div>

                    {/* Room Number Dropdown - Always show dropdown, even when no rooms available */}
                    <select
                      value={selectedRooms[idx] || ""}
                      onChange={(e) => handleRoomChange(idx, e.target.value)}
                      // disabled={fetchingRooms[idx]}
                      disabled={fetchingRooms[idx] || item.isPast}
                      style={{
                        flex: 1,
                        minWidth: "180px",
                        border: "1px solid #d1d5db",
                        borderRadius: "8px",
                        padding: "9px 12px",
                        fontSize: "13.5px",
                        color: "#111827",
                        backgroundColor: "#ffffff",
                        opacity: 1,
                        outline: "none",
                      }}
                    >
                      <option value="">
                        {fetchingRooms[idx]
                          ? "Loading rooms..."
                          : !roomsData[idx] || roomsData[idx].length === 0
                            ? "No rooms available"
                            : "Select Room No."}
                      </option>

                      {/* Only show rooms if available */}
                      {getAvailableRoomsForDropdown(idx).map((room) => (
                        <option key={room._id} value={room._id}>
                          {room.room_number}
                        </option>
                      ))}
                    </select>

                    {/* Show warning message only when there's an error or specific message */}
                    {roomAvailabilityMessage[idx] &&
                      roomsData[idx]?.length === 0 && (
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#dc2626",
                            fontWeight: 500,
                            marginTop: "4px",
                            width: "100%",
                          }}
                        >
                          ⚠️ {roomAvailabilityMessage[idx]}
                        </div>
                      )}
                  </div>
                )}
                {/* Grooming / Vet fields */}
                {isGroomingOrVet && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#111827",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Start Time
                    </span>

                    <div style={{ width: "170px" }}>
                      <select
                        value={selectedTimes[idx] || ""}
                        onChange={(e) => handleTimeChange(idx, e.target.value)}
                        disabled={item.isPast}
                        style={{
                          width: "100%",
                          border: "1px solid #d1d5db",
                          borderRadius: "8px",
                          padding: "9px 12px",
                          fontSize: "13.5px",
                          background: "#fff",
                        }}
                      >
                        <option value="">Select time</option>

                        {(item.parsedTimeSlots || []).map((slot, i) => (
                          <option key={i} value={slot}>
                            {slot}
                          </option>
                        ))}
                      </select>
                    </div>

                    <span
                      style={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#111827",
                        whiteSpace: "nowrap",
                        marginLeft: "8px",
                      }}
                    >
                      Allocate Staff
                    </span>

                    <div style={{ flex: 1 }}>
                      <StaffSelector
                        appointment={staffSelectorItem}
                        mode="update"
                        employees={employees}
                        excludeIds={getUnavailableStaffIds(idx)}
                        index={idx}
                        handleAppointmentFieldChange={handleStaffSelectorChange}
                      />
                    </div>
                  </div>
                )}
              </ServiceSection>
            );
          })}
        </div>

        {/* FOOTER */}
        <div
          style={{
            padding: "14px 18px",
            borderTop: "1px solid #f3f4f6",
            background: "#fff",
            display: "flex",
            justifyContent: "flex-end",
            gap: "12px",
            flexShrink: 0,
          }}
        >
          <button
            onClick={handleRejectOrder}
            disabled={isRejecting}
            style={{
              padding: "11px 28px",
              borderRadius: "10px",
              border: "none",
              cursor: isRejecting ? "not-allowed" : "pointer",
              background: isRejecting ? "#9ca3af" : "#dc2626",
              color: "#fff",
              fontWeight: 700,
              fontSize: "15px",
              display: "flex",
              alignItems: "center",
              gap: "7px",
            }}
          >
            <FaBan size={15} />
            {isRejecting ? "Rejecting..." : "Reject"}
          </button>
          <button
            onClick={handleAcceptOrder}
            disabled={isSaving}
            style={{
              padding: "11px 36px",
              borderRadius: "10px",
              border: "none",
              cursor: isSaving ? "not-allowed" : "pointer",
              background: isSaving ? "#9ca3af" : "#16a34a",
              color: "#fff",
              fontWeight: 800,
              fontSize: "16px",
            }}
          >
            {isSaving ? "Accepting..." : "Accept"}
          </button>
        </div>
      </div>
    </>
  );
};

export default OnlineOrderPopup;
